/** Create/edit form for a ContextBinding. Validated against `createContextBindingSchema`. */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  BINDING_TYPES,
  BINDING_ENTITY_TYPES,
  ACCESS_MODES,
  createContextBindingSchema,
  type CreateContextBindingInput,
} from "@aos/shared";

import { FormShell } from "../skills/_components/FormShell";
import {
  NumberField,
  optionsFromEnum,
  SelectField,
  TextField,
} from "../skills/_components/form";

export interface ContextBindingFormProps {
  action: "create" | "edit";
}

export function ContextBindingForm({ action }: ContextBindingFormProps) {
  const form = useForm<CreateContextBindingInput>({
    resolver: zodResolver(createContextBindingSchema),
    refineCoreProps: { resource: "context-bindings", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
  } = form;

  return (
    <FormShell
      title={action === "create" ? "New Context Binding" : "Edit Context Binding"}
      resource="context-bindings"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField
        form={form}
        name="context_id"
        label="Context ID"
        placeholder="cx_…"
      />
      <SelectField
        form={form}
        name="binding_type"
        label="Binding Type"
        options={optionsFromEnum(BINDING_TYPES)}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="entity_type"
          label="Entity Type"
          options={optionsFromEnum(BINDING_ENTITY_TYPES)}
        />
        <TextField
          form={form}
          name="entity_id"
          label="Entity ID"
          placeholder="art_… / pkg_… / rule_…"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="access_mode"
          label="Access Mode"
          options={optionsFromEnum(ACCESS_MODES)}
          allowEmpty
        />
        <NumberField
          form={form}
          name="priority"
          label="Priority"
          placeholder="50"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="active_from"
          label="Active From"
          placeholder="ISO-8601 (optional)"
        />
        <TextField
          form={form}
          name="active_to"
          label="Active To"
          placeholder="ISO-8601 (optional)"
        />
      </div>
    </FormShell>
  );
}

export default ContextBindingForm;
